import { capitalizeFirstLetter, isNullOrUndefined } from "src/common/utils";
import { IndividualUsersUniquePrincipalIdentifier } from "./information-asset-accesses";

export class AppliedScope {
  action: string;
  scope: string;

  constructor(arg1: any, arg2?: string) {
    if (typeof arg1 === "object") {
      if (arg1.action && !isNullOrUndefined(arg1.scope)) {
        this.action = arg1.action;
        this.scope = arg1.scope;
      } else throw new Error("Invalid applied scope");
    } else if (typeof arg1 === "string" && typeof arg2 === "string" && !isNullOrUndefined(arg2)) {
      this.action = arg1;
      this.scope = arg2;
    } else {
      throw new Error("Invalid applied scope");
    }
  }

  stringFormat(): string {
    return JSON.stringify(this);
  }

  displayFormat(): string {
    if (this.scope.length === 0) {
      return capitalizeFirstLetter(this.action);
    } else {
      return `${capitalizeFirstLetter(this.action)} - ${capitalizeFirstLetter(this.scope)}`;
    }
  }
}

export class AppliedScopeWithUserCount {
  appliedScope: AppliedScope;
  usersCount: number;

  constructor(appliedScope: AppliedScope, usersCount: number) {
    this.appliedScope = appliedScope;
    this.usersCount = usersCount;
  }

  displayFormat(): string {
    return `${this.appliedScope.displayFormat()} (${this.usersCount})`;
  }
}

export type GroupAppliedScopesByUsersCount = Array<AppliedScopeWithUserCount>;

export class CurrentAppliedScopesWithUsersMap {
  currentAppliedScopesWithUsers: Map<string, Set<string>>;

  constructor(currentAppliedScopesWithUsers?: Map<string, Set<string>>) {
    if (isNullOrUndefined(currentAppliedScopesWithUsers)) {
      this.currentAppliedScopesWithUsers = new Map();
    } else {
      this.currentAppliedScopesWithUsers = new Map(currentAppliedScopesWithUsers);
    }
  }

  set(appliedScope: string, stringifiedUsersFormat: Set<string> | undefined) {
    if (stringifiedUsersFormat) {
      this.currentAppliedScopesWithUsers.set(appliedScope, stringifiedUsersFormat);
    }
  }

  get(appliedScope: string): Set<string> | undefined {
    return this.currentAppliedScopesWithUsers.get(appliedScope);
  }

  has(appliedScope: string): boolean {
    return this.currentAppliedScopesWithUsers.has(appliedScope);
  }

  addUserToScope(appliedScope: AppliedScope, user: IndividualUsersUniquePrincipalIdentifier): void {
    if (this.has(JSON.stringify(appliedScope))) {
      this.set(
        JSON.stringify(appliedScope),
        this.get(JSON.stringify(appliedScope))?.add(JSON.stringify(user))
      );
    } else {
      this.set(JSON.stringify(appliedScope), new Set<string>().add(JSON.stringify(user)));
    }
  }

  addScopeIfNotPresent(appliedScope: AppliedScope): void {
    if (!this.has(JSON.stringify(appliedScope))) {
      this.set(JSON.stringify(appliedScope), new Set());
    }
  }

  combine(
    newCurrentAppliedScopesWithUsers: CurrentAppliedScopesWithUsersMap
  ): CurrentAppliedScopesWithUsersMap {
    const combinedCurrentAppliedScopesWithUsers = new CurrentAppliedScopesWithUsersMap(
      this.currentAppliedScopesWithUsers
    );
    newCurrentAppliedScopesWithUsers.currentAppliedScopesWithUsers.forEach(
      (usersForAppliedScopes: Set<string>, appliedScope: string) => {
        if (combinedCurrentAppliedScopesWithUsers.has(appliedScope)) {
          const combinedUsersForGivenScope: Set<string> = new Set();
          usersForAppliedScopes.forEach((user) => combinedUsersForGivenScope.add(user));
          combinedCurrentAppliedScopesWithUsers
            .get(appliedScope)
            ?.forEach((user) => combinedUsersForGivenScope.add(user));
          combinedCurrentAppliedScopesWithUsers.set(appliedScope, combinedUsersForGivenScope);
        } else {
          combinedCurrentAppliedScopesWithUsers.set(appliedScope, usersForAppliedScopes);
        }
      }
    );
    return combinedCurrentAppliedScopesWithUsers;
  }

  display(): string[] {
    const currentAppliedScopesWithCount: string[] = [];
    this.currentAppliedScopesWithUsers.forEach((users: Set<string>, appliedScope: string) => {
      if (users.size > 0) {
        currentAppliedScopesWithCount.push(
          `${new AppliedScope(JSON.parse(appliedScope)).displayFormat()} (${users.size})`
        );
      }
    });
    return currentAppliedScopesWithCount;
  }

  groupScopesByUsersCount(): GroupAppliedScopesByUsersCount {
    const groupByUsersCount: GroupAppliedScopesByUsersCount = [];
    this.currentAppliedScopesWithUsers.forEach((users: Set<string>, appliedScope: string) => {
      if (users.size > 0) {
        groupByUsersCount.push(
          new AppliedScopeWithUserCount(new AppliedScope(JSON.parse(appliedScope)), users.size)
        );
      }
    });
    return groupByUsersCount;
  }
}
